/**
 * Customer-facing price DISPLAY — the read-only projection of a resolved price.
 *
 * `menuPricingResolver.resolveItemPrice` answers *what an item costs on a given
 * ROOT→LEAF path*. This file answers the next question — *what a customer-facing
 * surface prints for that answer* — and it lives here, beside the resolver,
 * so the menu app, the board renderer, and online ordering cannot each grow
 * their own rendering of the same price.
 *
 * ── Why this is not just `formatPrice` ──────────────────────────────────────
 * Formatting turns 8.95 into "$8.95". This decides something earlier and more
 * consequential: whether there is a price to print AT ALL, whether it is one
 * number or a size ladder, and what a surface must do when resolution came back
 * with nothing usable.
 *
 * ── The honesty rule ────────────────────────────────────────────────────────
 * An item whose price cannot be resolved projects to `unavailable`, and a
 * surface renders that as ABSENT — no number, no placeholder.
 *
 * It specifically does NOT project to `market`. "Market Price" is a real
 * pricing concept in this system (`pricingStrategy: 'open'` — the cashier
 * enters the price), and the old menu app borrowed it as a catch-all: an item
 * configured `size_based` with an empty size list rendered as "Market Price" on
 * the wall. That is a claim the restaurant never made about a price the POS
 * will not charge. A surface that cannot honestly fill a field leaves it out —
 * the same rule `DeviceRuntime` states for reported hardware facts.
 *
 * Pure functions. No React, no Firestore, no currency formatting.
 */

import type { ResolvedItemPrice, ResolvedSize } from './menuPricingResolver';

/**
 * What a customer-facing surface should print for a resolved price.
 *
 *  - `single`      → one number.
 *  - `sizes`       → a size ladder, cheapest first. Always ≥ 2 entries; a
 *                    one-entry ladder is not a choice and collapses to `single`.
 *  - `market`      → the item is genuinely open-priced (`pricingStrategy: 'open'`).
 *  - `unavailable` → resolution produced nothing printable. Render NOTHING.
 */
export type MenuPriceDisplay =
  | { readonly kind: 'single'; readonly unitPrice: number }
  | { readonly kind: 'sizes'; readonly sizes: readonly ResolvedSize[] }
  | { readonly kind: 'market' }
  | { readonly kind: 'unavailable' };

/**
 * Project a resolved price into what a surface prints.
 *
 * @param resolved the output of {@link resolveItemPrice} for the path the
 *                 surface is displaying the item on. Callers MUST resolve
 *                 against a real ROOT→LEAF `menuGroupPath` first — reading
 *                 `item.basePrice` and projecting that is the bug this pair of
 *                 functions exists to make impossible.
 */
export function toMenuPriceDisplay(resolved: ResolvedItemPrice): MenuPriceDisplay {
  switch (resolved.kind) {
    case 'flat':
      // A resolved flat price is printable even at 0 — a free side with an
      // entree is a real menu line, and collapsing it into `unavailable` would
      // hide a real price instead of surfacing a real misconfiguration.
      return { kind: 'single', unitPrice: resolved.unitPrice };

    case 'sizes': {
      if (resolved.sizes.length === 0) {
        // `size_based` with no sizes configured. Nothing to print.
        return { kind: 'unavailable' };
      }
      if (resolved.sizes.length === 1) {
        // One size is not a choice. Print the number, drop the label.
        return { kind: 'single', unitPrice: resolved.sizes[0].price };
      }
      // Copy before sorting — the caller's array (and, through it, the resolver's
      // group size list) must not be reordered as a side effect of rendering.
      return {
        kind: 'sizes',
        sizes: [...resolved.sizes].sort((a, b) => a.price - b.price),
      };
    }

    case 'open':
      return { kind: 'market' };
  }
}
